import { printConfigAPI, receiptsAPI } from '@matix/api-client';
import {
  buildReprintData,
  buildReceiptHtml,
  printReceipt,
  printReportInvoice,
  usesReportInvoice,
} from '@matix/receipt';

/**
 * Print the sale invoice produced by converting an appointment, using the
 * cashier's exact receipt pipeline (`@matix/receipt`): same Receipt-Designer
 * layout, same tenant print options, same new-window printer that works on the
 * Android tablets.
 *
 * The cashier prints straight from live cart state; here the sale was created
 * server-side, so we read it back with `GET /api/v1/receipts/:id` — `convert`
 * returns `order_id`, which is the `Inv_Receipt_h.ID` that endpoint expects.
 * That is the same payload the till's own reprint reads, so the slip is built
 * by the same function (`buildReprintData`) rather than by a mapping of our
 * own — see the note at the mapping itself for what a second one had already
 * cost.
 *
 * Both endpoints sit behind `cashierAuth + tenantContext`, which the
 * appointments JWT satisfies, so no new backend surface is involved.
 */

/** Cached for the session — the config is static per tenant + branch. */
let printConfigPromise: ReturnType<typeof printConfigAPI.get> | null = null;

function loadPrintConfig(): ReturnType<typeof printConfigAPI.get> {
  if (!printConfigPromise) {
    printConfigPromise = printConfigAPI.get().catch((e: unknown) => {
      printConfigPromise = null; // let the next attempt retry
      throw e;
    });
  }
  return printConfigPromise;
}

/** Drop the cached print config (call on logout / branch switch). */
export function resetPrintConfigCache(): void {
  printConfigPromise = null;
}

export interface PrintSaleInvoiceArgs {
  /** `ConvertResult.order_id` — the Inv_Receipt_h row id. */
  receiptId: number;
  /** Fallback cashier identity when the receipt carries no employee. */
  fallbackCashier: { id: number; name: string | null };
}

/** The staff app's UI language, which decides the report edition + A4 wording. */
function currentLanguage(): 'ar' | 'en' {
  return localStorage.getItem('pos:appointments:lang') === 'ar' ? 'ar' : 'en';
}

export async function printSaleInvoice(args: PrintSaleInvoiceArgs): Promise<void> {
  const printConfig = await loadPrintConfig();

  // Report-server A4, when it is switched back on: pos-api renders it from
  // the receipt id alone, so there is nothing to read back or lay out here.
  // Off by default — the A4 sheet is laid out locally further down.
  if (usesReportInvoice(printConfig)) {
    await printReportInvoice(() =>
      receiptsAPI.getInvoiceReportPdf(args.receiptId, currentLanguage()),
    );
    return;
  }

  const detail = await receiptsAPI.getById(args.receiptId);

  // ONE mapping from a stored receipt to a slip, shared with the till, the
  // kitchen screen and the admin console. This file used to carry its own,
  // and it had already drifted: modifier names always printed in English, the
  // delivery fee never reached the totals, and named promotions were dropped.
  //
  // The language goes in here as well as into the renderer — modifiers are
  // flattened to one string per option before the renderer ever sees them.
  const lang = currentLanguage();
  const data = buildReprintData(printConfig, detail, lang);

  const html = buildReceiptHtml({
    ...data,
    // Three things this document does NOT share with a till reprint, and only
    // three:
    //
    //  - It is the FIRST print, not a reprint. `buildReprintData` stamps every
    //    slip it builds with the REPRINT stripe, because every other caller is
    //    reprinting; the copy handed to the customer at reception is the
    //    original and must not be marked as a duplicate.
    //  - A receipt written by `convert` can carry no employee (the conversion
    //    is not rung on a drawer), so the staff member who pressed the button
    //    is the fallback.
    //  - What was tendered is on the header here, and reception hands over
    //    change against it.
    isReprint: false,
    cashier: {
      id: detail.empoyee_id ?? args.fallbackCashier.id,
      name: detail.empoyee_name ?? args.fallbackCashier.name,
    },
    cashTendered: detail.paid_amount,
    language: lang,
  });

  // NumberOfReceiptPrint — mirrors the cashier receipt store's copy loop.
  const copies = Math.max(1, Math.min(9, printConfig.printOptions?.copies ?? 1));
  for (let i = 0; i < copies; i += 1) {
    await printReceipt(html);
  }
}
